/**
 * @docs docs/features/website-landing-experience
 */

import { definePageRoute } from '@common/router/definitions';
import React from 'react';
/*----------------------------------
- ROUTE: LANDING
----------------------------------*/

/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm packages

// App services

// App pages
import LandingPage from './page/LandingPage';
import type { LandingStats } from './types';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { buildHomeStructuredData, toStructuredDataFaqs, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import { buildHomeGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { HOME_FAQ_ITEMS } from '@/client/catalogs/landing/faq';
import { HOME_PAGE_TITLE } from '@/client/catalogs/landing/home/seo';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import {
  cachedLandingDomainsPreview,
  cachedLandingHeaderNavigation,
  cachedLandingStats,
} from '@/common/libs/connected/publicProductReadCache';
import { selectHomeDomainsPreview } from '@/client/pages/landing/lib/homeDomainsPreview';
import { HOME_DOMAINS_PREVIEW_RESULTS_LIMIT } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import type { WebsitePublicSearchResolution } from '@/common/types/domains/publicSearch';

/*----------------------------------
- HELPERS
----------------------------------*/

const toSafeStat = (value: number | string | null | undefined): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Math.round(numeric);
};
type TLandingStatsResponse = Partial<
  Record<
    | 'totalDomains'
    | 'totalTlds'
    | 'totalDataPoints'
    | 'totalSignups'
    | 'scannedToday'
    | 'updatedToday'
    | 'checkedLast24h'
    | 'updated',
    number | string | null
  >
>;

/*----------------------------------
- ROUTES
----------------------------------*/

export default definePageRoute({
    path: '/',
    options: {
  auth: false,
  layout: false,
  static: {
    refresh: '5 minutes',
    urls: ['/']
  }
},
    data: ({
  Product
}) => ({
  statsResponse: cachedLandingStats.read() ?? Product.Stats.general(),
  navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
  // The bare `/domains` search (ADR-0028 amendment, 2026-09-27): the same read and the same
  // projection the listing renders, so a Home row and a `/domains` row can never disagree. Product
  // cuts the list to ten rows before projecting, because this result is serialized into Home's HTML
  // as page data and a cut made after the read would leave the other rows in it. No filter catalog:
  // Home has no rail. The service normalizes the relative url.
  preview: cachedLandingDomainsPreview.read() ?? Product.Domains.search.ResolvePublicSearch({
    url: '/domains',
    includePreviewProjection: true,
    previewRowLimit: HOME_DOMAINS_PREVIEW_RESULTS_LIMIT,
  }),
}),
    render: ({
page,
  Router,
  statsResponse,
  navigation,
  preview
}: {
  page: {
    title?: string;
    head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
    jsonld: WebsiteJsonLdNode[];
  };
  Router: { url: (path: string) => string };
  statsResponse: TLandingStatsResponse;
  navigation: LandingHeaderDomainsNavigation;
  preview: WebsitePublicSearchResolution | null;
}) => {
  /*----------------------------------
  - INIT
  ----------------------------------*/

  // The resolved payload lands here, so this render seeds the public read
  // cache the data fetcher consults; see publicProductReadCache.ts.
  cachedLandingStats.seed(statsResponse);
  // An unready or empty resolution hides the section instead of answering 503 as `/domains` does,
  // and is not cached, so the next render retries it. A read that throws fails the render, exactly
  // like the stats and navigation reads above it.
  const domainsPreview = selectHomeDomainsPreview(preview);
  if (domainsPreview) cachedLandingDomainsPreview.seed(preview);

  const totalDomains = toSafeStat(statsResponse?.totalDomains);

  const stats: LandingStats = {
    totalDomains,
    totalTlds: toSafeStat(statsResponse?.totalTlds),
    // Home's funnel reads the scanner's own figure from the snapshot (batch 7 report, decision 28): a server
    // field, read as it comes, never a multiplier applied here.
    totalDataPoints: toSafeStat(statsResponse?.totalDataPoints),
    checkedLast24h: toSafeStat(statsResponse?.checkedLast24h),
    totalSignups: toSafeStat(statsResponse?.totalSignups),
    scannedToday: toSafeStat(statsResponse?.scannedToday),
    updatedToday: toSafeStat(statsResponse?.updatedToday)
  };
  // Set on both sides, so hydration keeps the server's title instead of the bare app name (catalogs/landing/home/seo.ts).
  page.title = HOME_PAGE_TITLE;
  page.head.push({
    $: 'link',
    rel: 'alternate',
    type: 'text/markdown',
    href: Router.url('/ai/pages/home.md'),
  });
  page.head.push({
    $: 'meta',
    name: 'robots',
    content: PUBLIC_INDEX_ROBOTS_DIRECTIVES,
  });
  const homeGeoContext = buildHomeGeoContext();
  page.jsonld.push(...buildHomeStructuredData(Router.url('/'), homeGeoContext, toStructuredDataFaqs(HOME_FAQ_ITEMS)));

  /*----------------------------------
  - RENDER
  ----------------------------------*/

  return (
    <LandingPage
      stats={stats}
      navigation={navigation}
      domainsPreview={domainsPreview}
    />
  );
},
});
